'use client';

import CustomButton from '@/components/custom-button';
import CustomInput from '@/components/custom-input';
import CustomForm, { FormItemProps } from '@/components/custom-RHF-form';
import { cn } from '@/lib/utils';
import { resetPasswordWithToken } from '@/server/api/v1/auth/auth.action';
import { ChangePasswordWithTokenRequest } from '@/server/api/v1/user/type';
import { ResetCodeType } from '@/types/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo, ReactNode, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { RePassword, RePasswordSchema } from './schema';

const defaultValues: RePassword = {
    old: '_default_',
    new: '',
    confirm_new: '',
};

type CustomRePasswordProps = {
    className?: string;

    username: string;
    token: string;

    /**callback change password sucess */
    onSuccess: () => Promise<void>;
    /**callback change password failed */
    onError: (
        errors: string[],
        /**
         * Nếu token được sử dụng và bị lỗi
         * thì lại load lại token mớimới
         */
        isReloadToken?: boolean
    ) => Promise<void>;

    alert?: ReactNode;
    onClearAlert: VoidFunction;
};

const CustomRePasswordWithToken = ({
    className,
    username,
    token,
    alert,
    onClearAlert,
    onSuccess,
    onError,
}: CustomRePasswordProps) => {
    const transDef = useTranslations('System');
    const transRePwd = useTranslations('RePassword');

    const [isSubmitting, setIsSubmitting] = useState(false);

    const methods = useForm<RePassword>({
        mode: 'onBlur',
        defaultValues: defaultValues,
        delayError: 500,
        resolver: zodResolver(RePasswordSchema(transDef)),
    });

    const onChangePassword = async (data: RePassword) => {
        // Nếu có truyền token thì gọi api đổi mật khẩu bằng token
        if (!!(token && username)) {
            onClearAlert();

            const sendData: ChangePasswordWithTokenRequest = {
                UserName: username,
                ResetCode: token,
                NewPassword: data.new,
                ResetCodeType: ResetCodeType.Token, // with token
            };

            setIsSubmitting(true);

            const result = await resetPasswordWithToken(sendData);

            if (result?.data?.isSuccessStatus) {
                onSuccess();
            } else {
                console.log({ result });
                await onError(result?.data?.errors ?? [], true);
                setIsSubmitting(false);
            }

            methods.reset(defaultValues);
        } else {
            onError(['Thông tin không hợp lệ!']);
        }

        setIsSubmitting(false);
    };

    const rls = useMemo<FormItemProps<RePassword>>(() => {
        const subRls: FormItemProps<RePassword> = [
            {
                type: 'Item',
                name: 'new',
                label: transRePwd('NewPassword'),
                required: true,
                wrapperClassName: 'col-span-full',
                children: (
                    <CustomInput.Password
                        className='no-ring'
                        placeholder={transDef('System_Enter') + '...'}
                        autoComplete='new-password'
                        readOnly={isSubmitting}
                    />
                ),
            },
            {
                type: 'Item',
                name: 'confirm_new',
                label: transRePwd('ConfirmPassword'),
                required: true,
                wrapperClassName: 'col-span-full',
                children: (
                    <CustomInput.Password
                        className='no-ring'
                        placeholder={transDef('System_Enter') + '...'}
                        autoComplete='new-password'
                        readOnly={isSubmitting}
                    />
                ),
            },
        ];

        return [
            {
                type: 'Grid',
                children: subRls,
            },
        ];
    }, [isSubmitting, transDef, transRePwd]);

    return (
        <CustomForm.FormProvider {...methods}>
            <div className={cn('max-w-xl', className)}>
                <CustomForm.Form rls={rls}>
                    {alert && <div className='mb-2'>{alert}</div>}

                    <div className='flex items-center justify-center gap-x-2 gap-y-1 pt-4'>
                        <CustomButton.Root
                            className={cn('min-w-32 max-md:flex-1')}
                            icon={<KeyRound size={16} />}
                            onClick={methods.handleSubmit(
                                onChangePassword,
                                (err) => {
                                    console.error(
                                        `change-password-form-failed`,
                                        err
                                    );
                                }
                            )}
                            isLoading={isSubmitting}
                        >
                            {transRePwd('ChangePassword')}
                        </CustomButton.Root>
                    </div>
                </CustomForm.Form>
            </div>
        </CustomForm.FormProvider>
    );
};

export default memo(CustomRePasswordWithToken);
